import React, { memo, useCallback, useEffect, useState } from "react";
import { useTranslate } from "helpers";
import { Button } from "ui/components/Button/Button";
import { OfficeEditFormValues, OfficeEmailRow } from "services/api/offices/types";
import { getOfficeEmailsAPI, deleteOfficeEmailAPI } from "services/api/offices/api";
import { initialDataWithPagination } from "services/api/consts";
import { Dialog } from "ui/components/Dialog";
import { TableSearch } from "ui/components/Table/TableSearch";
import OfficeEmailEditCard from "./OfficeEmailEditCard/OfficeEmailEditCard";
import { OFFICE_EMAIL_COLUMNS } from "./consts";

interface OfficeEmailStepProps {
  formValues: OfficeEditFormValues;
}

const OfficeEmailStep: React.FC<OfficeEmailStepProps> = ({ formValues: parentFormValues }) => {
  const officeId = parentFormValues.id;
  const { t } = useTranslate();
  const [emails, setEmails] = useState<OfficeEmailRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editEmailId, setEditEmailId] = useState<string | null>(null);
  const [deleteEmailId, setDeleteEmailId] = useState<string | null>(null);

  const fetchEmails = useCallback(async () => {
    if (!officeId) return;
    setIsLoading(true);
    try {
      const result = await getOfficeEmailsAPI(initialDataWithPagination.pagination, officeId);
      setEmails(result.data);
    } catch {
      setEmails([]);
    }
    setIsLoading(false);
  }, [officeId]);

  useEffect(() => {
    if (officeId) fetchEmails();
  }, [officeId]);

  const handleRowClick = useCallback((emailId: string) => {
    setEditEmailId(emailId);
    setShowEdit(true);
  }, []);

  const handleAddNew = () => {
    setEditEmailId(null);
    setShowEdit(true);
  };

  // Klik na koš ne smije otvoriti i uređivanje retka ispod njega.
  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, emailId: string) => {
      event.stopPropagation();
      event.preventDefault();

      setDeleteEmailId(emailId);
    },
    []
  );

  const handleDeleteConfirm = async () => {
    if (!officeId || !deleteEmailId) return;
    await deleteOfficeEmailAPI(t, officeId, deleteEmailId);
    setDeleteEmailId(null);
    fetchEmails();
  };

  const handleEditSuccess = () => {
    setShowEdit(false);
    setEditEmailId(null);
    fetchEmails();
  };

  return (
    <>
      <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Email adrese poslovnice</h1>
          <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
            Dodajte email adrese poslovnice klikom na gumb <strong>"Dodaj email"</strong>.
            Nakon unosa možete nastaviti na sljedeći korak.
          </p>
          {/* Uređivanje ide klikom na redak; prazno stanje nudi samo unos. */}
          {!isLoading && emails.length === 0 ? (
            <div className="max-w-md mx-auto">
              <Button
                variant="primary"
                size="full"
                startIcon="/media/icons/plus.svg"
                children="Dodaj email"
                onClick={handleAddNew}
              />
            </div>
          ) : (
            <>
              <div className="flex justify-end mb-3">
                <Button
                  variant="primary"
                  startIcon="/media/icons/plus.svg"
                  children="Dodaj email"
                  onClick={handleAddNew}
                />
              </div>

              <TableSearch
                columns={OFFICE_EMAIL_COLUMNS(handleDeleteClick)}
                data={emails}
                selectableRows={false}
                onRowClick={handleRowClick}
                isLoading={isLoading}
                disablePagination
              />
            </>
          )}
        </div>
      </div>

      <OfficeEmailEditCard
        officeId={officeId ?? ""}
        emailId={editEmailId}
        show={showEdit}
        onSuccess={handleEditSuccess}
        onClose={() => setShowEdit(false)}
      />
      <Dialog
        open={!!deleteEmailId}
        title="Brisanje email adrese"
        message="Jeste li sigurni da želite obrisati ovu email adresu?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteEmailId(null)}
      />
    </>
  );
};

export default memo(OfficeEmailStep);
